import { computed, nextTick, onMounted, Ref, ref, watch } from 'vue';
import { debounce, isNil, isObject } from 'lodash-es';
import { highlightAllUnder, highlight, languages } from 'prismjs';
import { useInternational } from '@/common/locale';
import router from '@/common/router';
import { standardTimeToUtcZeroTime, usePrismTheme } from '@/common/utils/commonUtils';
import { useFormatSqlText } from '@/common/composables/useFormatSqlText';
import { copyTextToClipboard } from '@/common/utils/browser';
import type { SqlTextParameter } from '@/database/utils/types';
import { useQuery } from '@tanstack/vue-query';
import { getShortenedText, splitHtmlByTagBoundary } from './utils';
import { useMessage } from '@/common/inject/useMessage';
import { FULL_TEXT_GETTER_OBJ, type DocumentTextParams } from '@/database/adapter';
import { SqlTextItem } from '@/api-client/mysql/model';
import { paExceptionHandler } from '@/common/utils/apiUtils';
import { FieldMissingError } from '@/database/adapter/adapters';

export interface Props {
  sqlTextParameter?: SqlTextParameter | null;
  isShow?: boolean;
  queryText?: string | null;
  className?: string;
  collapsed?: boolean;
  stretch?: boolean;
  dbType?: string;
  hideSqlTextFormatterButton?: boolean;
  noDataText?: string;
  errorText?: string;
}

export interface Emit {
  (e: 'update-text', value: { sqlFullText: string; sqlSampleText: string }): void;
}

export const useChunkHighlight = ({
  displaySqlText,
  fullTextComp,
  useLineNumbers = false,
  isFormatted = ref(false),
  isExpand = ref(true),
}: {
  displaySqlText: Ref<string>;
  fullTextComp: Ref<HTMLElement | undefined>;
  useLineNumbers?: boolean;
  isFormatted?: Ref<boolean>;
  isExpand?: Ref<boolean>;
}) => {
  const CHUNK_SIZE = 10000;

  const sqlTextHtml = computed(() => {
    return highlight(displaySqlText.value, languages.sql, 'sql');
  });
  const sqlTextHtmlChunks = computed(() => {
    return splitHtmlByTagBoundary(sqlTextHtml.value, CHUNK_SIZE);
  });

  const visibleChunkCount = ref(1);
  const visibleSqlTextHtmlChunks = computed(() => {
    return sqlTextHtmlChunks.value.slice(0, visibleChunkCount.value);
  });

  const highlightPrism = () => {
    if (!useLineNumbers) {
      return;
    }

    nextTick(() => {
      // line numbers 적용 하려면 highlight 호출 필요
      if (fullTextComp.value) {
        highlightAllUnder(fullTextComp.value);
      }
    });
  };

  const updateVisibleChunkCount = (count: number) => {
    visibleChunkCount.value = count;
    highlightPrism();
  };

  const onScroll = debounce((e: Event) => {
    const target = e.target as HTMLElement;

    const hasMoreChunks = visibleChunkCount.value < sqlTextHtmlChunks.value.length;
    if (!hasMoreChunks) {
      return;
    }

    const THRESHOLD = 20;

    const hasVerticalScroll = target.scrollHeight > target.clientHeight;
    const isVerticalScrollEnd =
      target.scrollTop + target.clientHeight >= target.scrollHeight - THRESHOLD;

    // 가로/세로 스크롤이 모두 있는 경우는 세로 스크롤만 고려하여 count 증가

    if (hasVerticalScroll) {
      if (isVerticalScrollEnd) {
        updateVisibleChunkCount(visibleChunkCount.value + 1);
      }
      return;
    }

    const isHorizontalScrollEnd =
      target.scrollLeft + target.clientWidth >= target.scrollWidth - THRESHOLD;

    if (isHorizontalScrollEnd) {
      updateVisibleChunkCount(visibleChunkCount.value + 1);
    }
  }, 150);

  // 스크롤이 생길 때까지 visibleChunkCount 증가
  watch([isFormatted, isExpand, displaySqlText], async ([curFormatted], [prevFormatted]) => {
    // 동일한 sql text에서 포매팅만 변경된 경우는 처리하지 않음
    if (curFormatted !== prevFormatted) {
      return;
    }

    updateVisibleChunkCount(1);

    const addUntilScrollShown = async () => {
      await nextTick();

      if (!fullTextComp.value) {
        return;
      }

      const hasVerticalScroll = fullTextComp.value.scrollHeight > fullTextComp.value.clientHeight;
      const hasHorizontalScroll = fullTextComp.value.scrollWidth > fullTextComp.value.clientWidth;
      const hasMoreChunks = visibleChunkCount.value < sqlTextHtmlChunks.value.length;

      if (hasMoreChunks && !(hasVerticalScroll || hasHorizontalScroll)) {
        updateVisibleChunkCount(visibleChunkCount.value + 1);
        await addUntilScrollShown();
      }
    };

    await addUntilScrollShown();
  });

  return {
    visibleSqlTextHtmlChunks,
    onScroll,
  };
};

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();
  const { showInfoMsg } = useMessage();
  const { formatSqlText } = useFormatSqlText();

  const isExpand = ref<boolean>(!props.collapsed);
  const isFormatted = ref<boolean>(false);

  const sqlText = ref<string>(props.queryText || '');
  const displaySqlText = ref<string>(props.queryText || '');
  const errorText = ref<string>('');

  const dbType = computed<string>(() => {
    const routeName = router.currentRoute.value?.name;
    let currentDbType = props.dbType;
    if (!currentDbType) {
      currentDbType = typeof routeName === 'string' ? routeName.split('_')[0] : '';
    }
    return currentDbType;
  });

  const queryParams = computed(() => {
    const { instanceId, sqlId, sqlTextHashId, xmSqlId, frameName, fromTime, toTime } =
      props.sqlTextParameter ?? {};

    return {
      instanceId: instanceId ?? '',
      sqlId: sqlId ?? '',
      normalizedSqlId: sqlId ?? '',
      sqlTextHashId,
      xmSqlId,
      fromTime: fromTime ? standardTimeToUtcZeroTime(fromTime.split(',')[0]) : undefined,
      toTime: toTime ? standardTimeToUtcZeroTime(toTime.split(',').pop() as string) : undefined,
      frameName: frameName ?? 'Full Text',
    };
  });

  const isQueryEnabled = computed<boolean>(() => {
    return (
      !!props.isShow &&
      isNil(props.queryText) &&
      !!props.sqlTextParameter &&
      !!FULL_TEXT_GETTER_OBJ[dbType.value ?? '']
    );
  });

  const {
    data: sqlTextResponse,
    isFetching: isLoading,
    error,
  } = useQuery<SqlTextItem>({
    queryKey: computed(() => ['sqlFullText', dbType.value, queryParams.value]),
    structuralSharing: false,
    queryFn: async () => {
      const { instanceId, frameName, fromTime, toTime } = queryParams.value;
      const fetchSqlTextValueFn = FULL_TEXT_GETTER_OBJ[dbType.value ?? ''];
      if (!fetchSqlTextValueFn) {
        return {};
      }

      const fullTextParams: DocumentTextParams = {
        instanceId,
        fromTime,
        toTime,
        detailInfo: queryParams.value,
        frameName,
        useFieldValidation: true,
      };

      const data = await fetchSqlTextValueFn(fullTextParams);
      if (!isObject(data)) {
        return { sqlText: data };
      }
      return data;
    },
    enabled: isQueryEnabled,
  });

  const isSqlTextLoading = computed<boolean>(
    () => (!!props.sqlTextParameter?.instanceId || !!props.queryText) && !isLoading.value,
  );

  const formattedSqlText = ref<string>('');

  const setDisplaySqlText = () => {
    isFormatted.value = false;
    formattedSqlText.value = '';

    if (props.collapsed && !isExpand.value) {
      displaySqlText.value = getShortenedText(sqlText.value);
    } else {
      displaySqlText.value = sqlText.value;
    }
  };

  const getFullDisplayText = () => {
    return isFormatted.value ? formattedSqlText.value : sqlText.value;
  };

  const clickExpand = () => {
    if (isExpand.value) {
      // 닫을 경우
      displaySqlText.value = getShortenedText(sqlText.value);
    } else {
      displaySqlText.value = getFullDisplayText();
    }
    isExpand.value = !isExpand.value;
  };

  const clickFormatting = () => {
    if (!isExpand.value || !isSqlTextLoading.value) {
      return;
    }
    if (isFormatted.value) {
      isFormatted.value = false;
    } else {
      const { text, success } = formatSqlText(sqlText.value, dbType.value);
      if (success) {
        isFormatted.value = true;
        formattedSqlText.value = text;
      }
    }
    displaySqlText.value = getFullDisplayText();
  };

  const clickCopyIcon = () => {
    if (!sqlText.value || !isSqlTextLoading.value) {
      return;
    }
    copyTextToClipboard(null, displaySqlText.value);
    showInfoMsg({ msgStr: t('NOTI.UI.COPY') });
  };

  // API 응답 처리
  watch(
    sqlTextResponse,
    (response) => {
      if (!response) {
        return;
      }

      errorText.value = '';
      sqlText.value = response.sqlText || '';
      const sqlSampleText = response.sqlSampleText || '';

      emit('update-text', { sqlFullText: sqlText.value, sqlSampleText });
      setDisplaySqlText();
    },
    { immediate: true },
  );

  watch(error, (err) => {
    if (!err) {
      errorText.value = '';
      return;
    }

    sqlText.value = '';
    setDisplaySqlText();

    if (err instanceof FieldMissingError) {
      errorText.value = t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', {
        type: '',
        value: err.value,
      });
      return;
    }

    paExceptionHandler({ error: err, isShowRejectMsg: true });
    errorText.value = '';
  });

  watch(
    [() => props.isShow, () => props.sqlTextParameter],
    ([isShow]) => {
      if (!isShow && !props.queryText) {
        sqlText.value = '';
        errorText.value = '';
        setDisplaySqlText();
      }
    },
    { deep: true },
  );

  onMounted(() => {
    setDisplaySqlText();
  });

  watch(
    () => props.queryText,
    (queryText) => {
      sqlText.value = queryText ?? '';
      setDisplaySqlText();
    },
  );

  usePrismTheme();

  const fullTextComp = ref<HTMLElement>();
  const { visibleSqlTextHtmlChunks, onScroll } = useChunkHighlight({
    displaySqlText,
    fullTextComp,
    isExpand,
    isFormatted,
  });

  return {
    t,

    isLoading,
    isSqlTextLoading,
    isExpand,
    displaySqlText,
    errorText,
    clickExpand,
    clickFormatting,
    clickCopyIcon,

    isFormatted,
    fullTextComp,
    visibleSqlTextHtmlChunks,
    onScroll,
  };
};
